import { getStoredAnalyticsConsent, isEEARegion } from './firebase';

// ============================================================
// Meta (Facebook / Instagram) Pixel
// ============================================================
// Docs: https://developers.facebook.com/docs/meta-pixel
//
// Same shape and same consent gate as oaiq.ts, for the same reason: fbevents.js
// drops its _fbp/_fbc cookies the moment it initialises, so it can't sit
// unconditionally in index.html. main.tsx calls initMetaPixel() on boot, while
// ?fbclid= is still in the URL - that is what the _fbc click cookie is built
// from, and it survives the round trip to checkout.stripe.com because it is a
// first-party romulus.app cookie.
//
// Differences from oaiq worth knowing:
//   - Amounts are in MAJOR units. $49 is 49, not 4900.
//   - PageView IS automatic after the first one: the pixel listens to
//     history.pushState, so SPA route changes are tracked without our help.
//     Firing it manually on route change as well would double-count.
//   - The browser pixel does not dedupe on eventID by itself (it only uses it
//     to match a server-side Conversions API event), so the sentEventIds guard
//     below is what stops the desktop buyer seen by /checkout AND /register
//     from counting as two purchases.

const PIXEL_ID = '1418538427133088';
const SDK_URL = 'https://connect.facebook.net/en_US/fbevents.js';

type FbqFn = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: FbqFn;
  loaded: boolean;
  version: string;
};

type MetaWindow = Window & { fbq?: FbqFn; _fbq?: FbqFn };

const getWindow = (): MetaWindow | null =>
  typeof window === 'undefined' ? null : (window as MetaWindow);

const consentGranted = (): boolean =>
  (getStoredAnalyticsConsent() ?? (isEEARegion() ? 'denied' : 'granted')) === 'granted';

// The vendor snippet, transcribed.
const loadSdk = (): void => {
  const w = getWindow();
  if (!w || w.fbq) return;
  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  } as FbqFn;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  const script = w.document.createElement('script');
  script.async = true;
  script.src = SDK_URL;
  const first = w.document.getElementsByTagName('script')[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else w.document.head.appendChild(script);
};

let initialised = false;
let hashedEmail: string | null = null;

export const initMetaPixel = (): void => {
  if (initialised || !PIXEL_ID) return;
  const w = getWindow();
  if (!w) return;
  // Local/emulator traffic must not land in the ad account.
  if (import.meta.env.DEV || import.meta.env.VITE_USE_EMULATORS === 'true') return;
  if (!consentGranted()) return;
  try {
    loadSdk();
    w.fbq?.('init', PIXEL_ID, hashedEmail ? { em: hashedEmail } : {});
    w.fbq?.('track', 'PageView');
    initialised = true;
  } catch (err) {
    console.warn('[meta] init failed', err);
  }
};

const sentEventIds = new Set<string>();

const track = (event: string, data: Record<string, unknown>, eventId?: string): void => {
  try {
    initMetaPixel();
    if (!initialised) return;
    if (eventId) {
      if (sentEventIds.has(eventId)) return;
      sentEventIds.add(eventId);
    }
    const w = getWindow();
    if (eventId) w?.fbq?.('track', event, data, { eventID: eventId });
    else w?.fbq?.('track', event, data);
  } catch (err) {
    console.warn('[meta] track failed', err);
  }
};

/** Fired when the buyer reaches a real checkout - embedded form or hosted redirect. */
export const metaInitiateCheckout = (planId: string, planName: string, amountCents: number): void => {
  track('InitiateCheckout', {
    value: amountCents / 100,
    currency: 'USD',
    content_ids: [planId],
    content_name: planName,
    content_type: 'product',
    num_items: 1,
  });
};

/**
 * The money event - what the ad set optimises for. `eventId` must be the same
 * stable Stripe object id oaiqOrderCreated gets, so a future server-side
 * Conversions API call dedupes against it.
 */
export const metaPurchase = (args: {
  eventId: string;
  planId: string;
  planName: string;
  amountCents: number;
}): void => {
  track(
    'Purchase',
    {
      value: args.amountCents / 100,
      currency: 'USD',
      content_ids: [args.planId],
      content_name: args.planName,
      content_type: 'product',
      num_items: 1,
    },
    args.eventId,
  );
};

/**
 * Advanced matching: the SHA-256 of the normalised email, re-sent via init.
 * Meta accepts pre-hashed `em`, so the raw address never leaves the page.
 */
export const setMetaUserEmail = async (email: string): Promise<void> => {
  try {
    const normalised = email.trim().toLowerCase();
    if (!normalised || !window.crypto?.subtle) return;
    const bytes = new TextEncoder().encode(normalised);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    if (hex === hashedEmail) return;
    hashedEmail = hex;
    if (initialised) getWindow()?.fbq?.('init', PIXEL_ID, { em: hex });
    else initMetaPixel();
  } catch (err) {
    console.warn('[meta] user match failed', err);
  }
};

/**
 * The pixel's _fbp (browser) and _fbc (ad click) cookies, for the checkout
 * callables to stamp on the Stripe object. The Stripe webhook that later sends
 * the server-side Purchase (functions/src/utils/metaCapi.ts) has no cookies of
 * its own, and without _fbc Meta can't tie a sale back to the click.
 */
export const getMetaBrowserIds = (): { metaFbp?: string; metaFbc?: string } => {
  try {
    const out: { metaFbp?: string; metaFbc?: string } = {};
    for (const part of document.cookie.split(';')) {
      const [k, ...v] = part.trim().split('=');
      if (k === '_fbp') out.metaFbp = v.join('=');
      if (k === '_fbc') out.metaFbc = v.join('=');
    }
    return out;
  } catch {
    return {};
  }
};
